/**
 * Subscription & Pricing Data for Visionary Code Platform
 * 
 * Tier Structure:
 * - Python is the base language (lowest cost)
 * - Other languages (JS, React) cost progressively more
 * - Bundles offer discounts for combined courses
 * - Group/District rates for schools and TVIs
 * - Scholarship options for qualifying students
 */

// ─── Individual Language Subscriptions ──────────────────────────
export const languageSubscriptions = [
  {
    id: "python-starter",
    language: "Python",
    icon: "FaPython",
    color: "#3776AB",
    tier: "starter",
    label: "Python Starter",
    tagline: "Begin your coding journey",
    monthlyPrice: 9.99,
    annualPrice: 99.99,
    features: [
      "10 interactive Python modules",
      "Coding challenges with accessible editor",
      "Multiple choice quizzes per module",
      "Screen reader optimized content",
      "Audio feedback and navigation sounds",
      "Progress tracking and certificates",
    ],
    accessibilityNote:
      "Fully screen-reader compatible. All content designed for NVDA, JAWS, and VoiceOver.",
    recommended: false,
  },
  {
    id: "javascript-pro",
    language: "JavaScript",
    icon: "FaJs",
    color: "#F7DF1E",
    tier: "pro",
    label: "JavaScript Pro",
    tagline: "Build interactive web experiences",
    monthlyPrice: 14.99,
    annualPrice: 149.99,
    features: [
      "10-day JavaScript mastery curriculum",
      "DOM manipulation & event handling",
      "Async/Await & Fetch API lessons",
      "Accessible coding challenges",
      "Screen reader optimized content",
      "Progress tracking and certificates",
      "Priority email support",
    ],
    accessibilityNote:
      "Includes lessons on building accessible JavaScript applications and understanding ARIA roles.",
    recommended: false,
  },
  {
    id: "react-premium",
    language: "React",
    icon: "FaReact",
    color: "#61DAFB",
    tier: "premium",
    label: "React Premium",
    tagline: "Master modern UI development",
    monthlyPrice: 19.99,
    annualPrice: 199.99,
    features: [
      "10-day React deep-dive curriculum",
      "Component architecture & Hooks",
      "State management & Context API",
      "React Router & navigation",
      "Accessible coding challenges",
      "Screen reader optimized content",
      "Progress tracking and certificates",
      "Priority email & chat support",
      "Live code examples",
    ],
    accessibilityNote:
      "Covers building accessible React components with proper ARIA attributes and focus management.",
    recommended: false,
  },
];

// ─── Curriculum Bundles ─────────────────────────────────────────
export const curriculumBundles = [
  {
    id: "web-foundations-bundle",
    title: "Web Foundations Bundle",
    subtitle: "HTML + CSS + JavaScript",
    tagline: "Build accessible websites from scratch",
    description:
      "The perfect starting point for visually impaired learners. Start with text-based languages (HTML & CSS) to build real web pages, then add interactivity with JavaScript. Every lesson teaches proper screen reader support and accessibility best practices.",
    icon: "FaCode",
    color: "#667EEA",
    gradient: "linear(to-br, #667EEA, #764BA2)",
    monthlyPrice: 24.99,
    annualPrice: 249.99,
    savings: "Save $49.98/year vs individual plans",
    courses: ["html", "css", "javascript"],
    features: [
      "Complete HTML curriculum — structure, semantics, ARIA",
      "Full CSS course — styling, layout, responsive design",
      "JavaScript fundamentals — DOM, events, async",
      "Accessibility-first approach in every lesson",
      "Learn how screen readers parse HTML",
      "Build a fully accessible portfolio website",
      "30 coding challenges across all 3 languages",
      "Downloadable reference guides (screen reader friendly)",
      "Certificate of completion per course",
    ],
    accessibilityHighlights: [
      "Understand how screen readers interpret HTML tags",
      "Learn semantic HTML for better assistive technology support",
      "Practice ARIA roles, states, and properties hands-on",
      "Build pages that are tested with NVDA and VoiceOver",
      "CSS techniques for high contrast and low vision support",
    ],
    targetAudience:
      "Ideal for low-vision students and Teachers of the Visually Impaired (TVIs) who want a structured, accessible web development curriculum.",
    popular: true,
  },
  {
    id: "full-stack-accessible-bundle",
    title: "Full-Stack Accessible Bundle",
    subtitle: "HTML + CSS + JavaScript + React + Python",
    tagline: "The complete accessible developer toolkit",
    description:
      "Everything in the Web Foundations Bundle, plus React for modern UI development and Python for data and automation. This bundle covers the full spectrum of accessible software development.",
    icon: "FaRocket",
    color: "#F56565",
    gradient: "linear(to-br, #F56565, #ED8936)",
    monthlyPrice: 39.99,
    annualPrice: 399.99,
    savings: "Save $79.89/year vs individual plans",
    courses: ["html", "css", "javascript", "react", "python"],
    features: [
      "Everything in Web Foundations Bundle",
      "Full Python Masterclass (10 modules)",
      "React 10-Day Learning curriculum",
      "Cross-language accessibility patterns",
      "Build accessible full-stack applications",
      "50+ coding challenges across all 5 languages",
      "Priority support via email and chat",
      "Monthly live Q&A sessions (accessible)",
      "Certificate of completion per course",
    ],
    accessibilityHighlights: [
      "All items from Web Foundations",
      "React accessibility patterns (focus traps, live regions)",
      "Python automation for accessibility testing",
      "End-to-end accessible app development",
    ],
    targetAudience:
      "For motivated learners and districts looking for a comprehensive, multi-language accessible coding curriculum.",
    popular: false,
  },
];

// ─── Mentoring Subscriptions ────────────────────────────────────
export const mentoringPlans = [
  {
    id: "mentoring-individual",
    title: "1-on-1 Mentoring",
    tagline: "Personal guidance from accessibility-trained mentors",
    description:
      "Work directly with a mentor who understands accessible coding and assistive technology. Sessions are conducted via accessible video conferencing with screen-sharing.",
    monthlyPrice: 79.99,
    annualPrice: 799.99,
    icon: "FaUserFriends",
    color: "#38B2AC",
    features: [
      "2 live sessions per month (45 min each)",
      "Mentor trained in screen reader workflows",
      "Personalized learning plan",
      "Code review with accessibility audit",
      "Email support between sessions",
      "Session recordings (audio described)",
    ],
    popular: false,
  },
  {
    id: "mentoring-intensive",
    title: "Intensive Mentoring",
    tagline: "Accelerated learning with weekly sessions",
    description:
      "For learners who want rapid progress. Weekly 1-on-1 sessions with dedicated mentors, plus access to all course content.",
    monthlyPrice: 149.99,
    annualPrice: 1499.99,
    icon: "FaChalkboardTeacher",
    color: "#805AD5",
    features: [
      "4 live sessions per month (60 min each)",
      "Dedicated mentor assignment",
      "All course bundles included",
      "Custom project mentorship",
      "Priority scheduling",
      "Session recordings (audio described)",
      "Direct messaging with your mentor",
      "Career guidance and portfolio review",
    ],
    popular: true,
  },
];

// ─── Group & District Rates ─────────────────────────────────────
export const groupRates = {
  title: "Group & District Licensing",
  subtitle: "Scalable pricing for schools, districts, and organizations",
  description:
    "Empower Teachers of the Visually Impaired (TVIs) with a curriculum they can support and scale. Districts get volume pricing, a teacher admin dashboard, and dedicated onboarding.",
  icon: "FaSchool",
  benefits: [
    "Volume discounts starting at 5 seats",
    "Teacher admin dashboard for progress monitoring",
    "Curriculum aligned to accessibility standards",
    "Dedicated district onboarding specialist",
    "Custom reporting for IEP documentation",
    "Professional development credits for TVIs",
    "Bulk scholarship allocation tools",
  ],
  tiers: [
    {
      id: "group-small",
      label: "Small Group",
      seats: "5–15 students",
      discount: "15% off",
      perSeatMonthly: 8.49,
      description: "Perfect for individual classrooms or small programs.",
    },
    {
      id: "group-medium",
      label: "School License",
      seats: "16–50 students",
      discount: "25% off",
      perSeatMonthly: 7.49,
      description: "Covers a full school program with teacher tools.",
    },
    {
      id: "group-large",
      label: "District License",
      seats: "51–200 students",
      discount: "35% off",
      perSeatMonthly: 6.49,
      description: "District-wide deployment with dedicated support.",
    },
    {
      id: "group-enterprise",
      label: "Enterprise / State",
      seats: "200+ students",
      discount: "Custom",
      perSeatMonthly: null,
      description:
        "Custom pricing, dedicated success manager, and implementation services.",
      contactSales: true,
    },
  ],
  sellingPoints: [
    {
      title: "Built for TVIs",
      description:
        "The curriculum is designed so Teachers of the Visually Impaired can support students independently after initial setup.",
    },
    {
      title: "District ROI",
      description:
        "Districts invest in accessibility-first STEM education that meets IEP goals and prepares students for tech careers.",
    },
    {
      title: "Scalable Infrastructure",
      description:
        "From a single classroom to an entire state — the platform scales with your needs.",
    },
  ],
};

// ─── Scholarship Program ────────────────────────────────────────
export const scholarshipProgram = {
  title: "Visionary Scholars Program",
  subtitle: "Making accessible coding education available to all",
  description:
    "We believe financial barriers should never prevent a visually impaired student from learning to code. Our scholarship program offers partial and full tuition assistance.",
  icon: "FaGraduationCap",
  tiers: [
    {
      id: "scholarship-partial",
      label: "Partial Scholarship",
      discount: "50% off",
      eligibility: [
        "Enrolled in a K-12 or higher education program",
        "Documented visual impairment",
        "Recommendation from a TVI or counselor",
      ],
    },
    {
      id: "scholarship-full",
      label: "Full Scholarship",
      discount: "100% — Free Access",
      eligibility: [
        "All partial scholarship requirements",
        "Demonstrated financial need",
        "Short essay on learning goals",
      ],
    },
  ],
  applicationProcess: [
    "Complete the online scholarship application form",
    "Provide documentation of visual impairment",
    "Submit a recommendation from your TVI or school counselor",
    "Write a brief statement about your coding goals (200 words)",
    "Applications reviewed within 5 business days",
  ],
  impactStatement:
    "Since launch, 120+ students have received scholarships, many entering their first STEM career pathway.",
};

// ─── New Course Data: HTML & CSS ─────────────────────────────────
export const accessibilityCourses = {
  html: {
    id: "html",
    title: "HTML & Accessibility Fundamentals",
    description:
      "Learn HTML from the ground up with a focus on semantic structure and screen reader support. Understand how assistive technologies parse web pages.",
    videos: [
      {
        id: 1,
        title: "Module 1: What is HTML?",
        description:
          "Introduction to HTML — what it is, how browsers read it, and why it matters for accessibility. Understand the relationship between HTML and screen readers.",
        youtubeId: "qz0aGYrrlhU",
      },
      {
        id: 2,
        title: "Module 2: HTML Document Structure",
        description:
          "Learn the basic structure of an HTML document: DOCTYPE, html, head, and body tags. Understand how screen readers navigate document landmarks.",
        youtubeId: "UB1O30fR-EE",
      },
      {
        id: 3,
        title: "Module 3: Headings, Paragraphs & Text",
        description:
          "Use headings (h1-h6) to create a clear content hierarchy. Learn how screen reader users navigate by headings to quickly find content.",
        youtubeId: "yqcd-XkxZNM",
      },
      {
        id: 4,
        title: "Module 4: Links & Navigation",
        description:
          "Create accessible links with descriptive text. Learn why 'Click here' is bad for screen readers and how to build keyboard-navigable menus.",
        youtubeId: "gqOEoUR5RHg",
      },
      {
        id: 5,
        title: "Module 5: Images & Alt Text",
        description:
          "Add images to web pages with proper alt attributes. Learn the difference between decorative and informative images for screen reader users.",
        youtubeId: "_w6N_nplmAw",
      },
      {
        id: 6,
        title: "Module 6: Lists & Tables",
        description:
          "Structure data with ordered lists, unordered lists, and tables. Understand how screen readers announce table headers and cells.",
        youtubeId: "HeQfRnVhU3I",
      },
      {
        id: 7,
        title: "Module 7: Forms & Input Accessibility",
        description:
          "Build accessible forms with proper labels, fieldsets, and error messages. Learn how screen readers handle form controls.",
        youtubeId: "fF9ykfHWkPk",
      },
      {
        id: 8,
        title: "Module 8: Semantic HTML5 Elements",
        description:
          "Use header, nav, main, article, section, aside, and footer elements. Understand how landmarks help screen reader users navigate pages efficiently.",
        youtubeId: "kGW8Al_cga4",
      },
      {
        id: 9,
        title: "Module 9: ARIA Roles & Attributes",
        description:
          "Introduction to WAI-ARIA: roles, states, and properties. Learn when to use ARIA and when native HTML semantics are sufficient.",
        youtubeId: "0hqhAIjE_8I",
      },
      {
        id: 10,
        title: "Module 10: Building a Screen Reader Friendly Page",
        description:
          "Capstone project: build a complete, accessible web page from scratch. Test with a screen reader and validate against WCAG guidelines.",
        youtubeId: "aqM6rV5IBlg",
      },
    ],
  },
  css: {
    id: "css",
    title: "CSS for Accessible Design",
    description:
      "Learn CSS with a focus on creating visually accessible, responsive designs. Covers high contrast, focus indicators, and low-vision-friendly techniques.",
    videos: [
      {
        id: 1,
        title: "Module 1: Introduction to CSS",
        description:
          "What is CSS and how it works with HTML. Learn inline, internal, and external stylesheets. Understand how CSS affects screen reader behavior.",
        youtubeId: "yfoY53QXEnI",
      },
      {
        id: 2,
        title: "Module 2: Colors, Contrast & Readability",
        description:
          "Choose accessible color combinations that meet WCAG contrast ratios. Learn tools to check color contrast for low-vision users.",
        youtubeId: "HY8q4TD3KGM",
      },
      {
        id: 3,
        title: "Module 3: Typography & Readable Text",
        description:
          "Select accessible fonts, sizes, and line-heights. Understand how font choices impact readability for users with low vision.",
        youtubeId: "RNakAX3rVVw",
      },
      {
        id: 4,
        title: "Module 4: Box Model & Layout Basics",
        description:
          "Understand margin, padding, border, and width. Learn how the box model affects the visual layout of accessible components.",
        youtubeId: "rIO5326FgPE",
      },
      {
        id: 5,
        title: "Module 5: Flexbox for Accessible Layouts",
        description:
          "Build flexible, responsive layouts with Flexbox. Learn how visual order vs DOM order affects screen reader experience.",
        youtubeId: "u044iM9xsWU",
      },
      {
        id: 6,
        title: "Module 6: CSS Grid & Responsive Design",
        description:
          "Create complex layouts with CSS Grid. Build mobile-first, responsive pages that work with zoom and screen magnifiers.",
        youtubeId: "EiNiSFIPIQE",
      },
      {
        id: 7,
        title: "Module 7: Focus Styles & Keyboard Visibility",
        description:
          "Design visible focus indicators for keyboard users. Learn why removing outlines is harmful and how to create beautiful focus styles.",
        youtubeId: "Mvu5OMGcdVA",
      },
      {
        id: 8,
        title: "Module 8: Animations & Reduced Motion",
        description:
          "Add CSS animations and transitions safely. Respect the prefers-reduced-motion media query to prevent motion sickness.",
        youtubeId: "N5EW4HnF6FU",
      },
      {
        id: 9,
        title: "Module 9: CSS for Screen Reader Content",
        description:
          "Use CSS to visually hide content while keeping it accessible to screen readers. The sr-only pattern and skip-navigation links.",
        youtubeId: "QW_dUs9D1oQ",
      },
      {
        id: 10,
        title: "Module 10: Building an Accessible Portfolio",
        description:
          "Capstone project: style your HTML portfolio with accessible CSS. High contrast mode, responsive design, and WCAG AA compliance.",
        youtubeId: "oYRda7UtuhA",
      },
    ],
  },
};
